import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import ApiService from 'services/apiService';
import { SchoolEnrollmentData, SchoolEnrollmentContextValue } from '../types';

const SchoolEnrollmentDataContext = createContext<SchoolEnrollmentContextValue | undefined>(
    undefined
);

interface SchoolEnrollmentDataProviderProps {
    children: React.ReactNode;
    schoolYear: number;
}

const determineSchoolType = (
    schoolName?: string
): 'Middle' | 'High' | 'Elementary' | 'Other' => {
    if (!schoolName) return 'Other';

    const lowerName = schoolName.toLowerCase();

    if (lowerName.includes('middle')) return 'Middle';
    if (lowerName.includes('high')) return 'High';
    if (lowerName.includes('elementary')) return 'Elementary';

    return 'Other';
};

export const SchoolEnrollmentDataProvider: React.FC<SchoolEnrollmentDataProviderProps> = ({
    children,
    schoolYear,
}) => {
    const [data, setData] = useState<SchoolEnrollmentData[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const fetchEnrollmentData = useCallback(async () => {
    try {
        setLoading(true);
        setError(null);

      const enrollmentsResponse = await ApiService.studentEnrollments.getList({
            page: 1,
            pageSize: 10000,
            enrollmentFilter: {
                schoolYears: [schoolYear]
            }
        });
        
        if (!enrollmentsResponse || enrollmentsResponse.length === 0) {
            setData([]);
            return;
        }
        
        // Group by school name and count
        const schoolGroups = new Map<string, number>();
        enrollmentsResponse.forEach((enrollment) => {
            const schoolName = enrollment.school || 'Unknown School';
            schoolGroups.set(schoolName, (schoolGroups.get(schoolName) || 0) + 1);
        });
        
        // Convert to array and sort
        const enrollmentData = Array.from(schoolGroups.entries())
            .map(([schoolName, count]) => ({
                schoolId: 0,
                schoolName,
                enrollmentCount: count,
                schoolType: determineSchoolType(schoolName),
            }))
            .sort((a, b) => b.enrollmentCount - a.enrollmentCount)
            .slice(0, 10);
        
        setData(enrollmentData);
    } catch (err) {
        console.error('Failed to fetch school enrollment data:', err);
        setError('Failed to load enrollment data');
        setData([]);
    } finally {
        setLoading(false);
    }
  }, [schoolYear]);

    useEffect(() => {
        fetchEnrollmentData();
    }, [fetchEnrollmentData]);

    return (
        <SchoolEnrollmentDataContext.Provider value={{ data, loading, error }}>
            {children}
        </SchoolEnrollmentDataContext.Provider>
    );
};

export const useSchoolEnrollmentData = (): SchoolEnrollmentContextValue => {
    const context = useContext(SchoolEnrollmentDataContext);

    if (!context) {
        throw new Error(
            'useSchoolEnrollmentData must be used within SchoolEnrollmentDataProvider'
        );
    }

    return context;
};